import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { AlertModal, AlertType } from '@/components/AlertModal'
import { InventoryFeedCardSkeleton, Skeleton } from '@/components/SkeletonLoader'
import Pagination, { paginate } from '@/components/Pagination'
import { Plus, Edit, Trash2, Package, X, List, RefreshCw } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'

export default function InventoryPage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()

  const [feedPage, setFeedPage] = useState(1)

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    type: AlertType
    title: string
    message: string
    onConfirm?: () => void
    confirmText?: string
    cancelText?: string
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  })

  const [editingFeedId, setEditingFeedId] = useState<number | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [viewingFeed, setViewingFeed] = useState<any>(null)
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [selectedPlatform, setSelectedPlatform] = useState<string>('')

  // Check if we need to open edit modal from navigation state
  useEffect(() => {
    const state = location.state as { openEditModal?: string | number } | null
    if (state?.openEditModal) {
      const feedId = typeof state.openEditModal === 'string' ? parseInt(state.openEditModal) : state.openEditModal
      if (feedId) {
        setEditingFeedId(feedId)
        // Clear the state to prevent reopening on re-render
        navigate(location.pathname, { replace: true, state: {} })
      }
    }
  }, [location.state, navigate, location.pathname])

  // Fetch inventory feeds
  const { data: feedsData, isLoading, error } = useQuery({
    queryKey: ['inventory-feeds', vendorId],
    queryFn: () => api.inventory.listFeeds({ vendorId: vendorId?.toString() }),
    enabled: !!vendorId,
  })

  const feeds = feedsData?.data?.data?.items || feedsData?.data?.items || []
  
  // Debug: Log feeds to check platform field
  useEffect(() => {
    if (feeds.length > 0) {
      console.log('Feeds with platform:', feeds.map((f: any) => ({ 
        feedId: f.feedId, 
        platform: f.platform,
        merchant: f.merchant?.name 
      })))
    }
  }, [feeds])

  // Add feed mutation
  const createFeedMutation = useMutation({
    mutationFn: (data: any) => api.inventory.createFeed(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-feeds', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['inventory-feeds'] })
      setShowCreateModal(false)
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: 'Feed added successfully!',
      })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error?.message || 'Failed to add feed',
      })
    },
  })

  // Update feed mutation
  const updateFeedMutation = useMutation({
    mutationFn: ({ feedId, data }: { feedId: string; data: any }) =>
      api.inventory.updateFeed(feedId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-feeds'] })
      queryClient.invalidateQueries({ queryKey: ['inventory-feeds', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['inventory-feed', editingFeedId] })
      setEditingFeedId(null)
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: 'Feed updated successfully!',
      })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error?.message || 'Failed to update feed',
      })
    },
  })

  // Delete feed mutation
  const deleteFeedMutation = useMutation({
    mutationFn: (feedId: string) => api.inventory.deleteFeed(feedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-feeds'] })
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: 'Feed deleted successfully!',
      })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error?.message || 'Failed to delete feed',
      })
    },
  })

  // Sync feed mutation
  const syncFeedMutation = useMutation({
    mutationFn: (data: { platform: string; vendorId?: number }) => api.inventory.syncFeed(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['inventory-feeds', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['inventory-feeds'] })
      setShowSyncModal(false)
      setSelectedPlatform('')
      const data = response.data?.data || response.data
      const syncedCount = data?.synced_count || 0
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: data?.message || `Successfully synced ${syncedCount} feed(s) from ${selectedPlatform}`,
      })
    },
    onError: (error: any) => {
      // Close the sync modal on error
      setShowSyncModal(false)
      setSelectedPlatform('')
      // Extract error message
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to sync feeds'
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage,
        confirmText: 'OK',
      })
    },
  })

  // View feed
  const { data: feedData, isLoading: isLoadingFeed } = useQuery({
    queryKey: ['inventory-feed', viewingFeed],
    queryFn: () => api.inventory.getFeed(viewingFeed),
    enabled: !!viewingFeed,
  })

  // Fetch full feed data for editing
  const { data: editFeedData, isLoading: isLoadingEditFeed } = useQuery({
    queryKey: ['inventory-feed', editingFeedId],
    queryFn: () => api.inventory.getFeed(editingFeedId?.toString()),
    enabled: !!editingFeedId,
  })

  const handleDelete = (feed: any) => {
    setAlertModal({
      isOpen: true,
      type: 'warning',
      title: 'Delete Feed',
      message: `Are you sure you want to delete the feed for ${feed.merchant?.name || 'this vendor'}? This action cannot be undone.`,
      onConfirm: () => {
        deleteFeedMutation.mutate(feed.feedId)
      },
      confirmText: 'Delete',
      cancelText: 'Cancel',
    })
  }

  const handleSubmit = (formData: any) => {
    if (editingFeedId) {
      updateFeedMutation.mutate({ feedId: editingFeedId.toString(), data: formData })
    } else {
      createFeedMutation.mutate({ ...formData, vendorId })
    }
  }

  if (isLoading) {
    return (
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <Skeleton height="2rem" width="300px" style={{ marginBottom: '0.5rem', borderRadius: '4px' }} />
            <Skeleton height="1rem" width="400px" style={{ borderRadius: '4px' }} />
          </div>
          <Skeleton height="2.5rem" width="140px" style={{ borderRadius: '4px' }} />
        </div>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(350px, 100%), 1fr))', gap: '1.5rem' }}>
          <InventoryFeedCardSkeleton />
          <InventoryFeedCardSkeleton />
          <InventoryFeedCardSkeleton />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container">
        <div className="error-message">
          Failed to load feeds. Please try again later.
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        marginBottom: '2rem',
        gap: isMobile ? '1rem' : '0'
      }}>
        <div>
          <h1 style={{ marginBottom: '0.5rem', fontSize: isMobile ? '1.5rem' : '2rem' }}>Feeds</h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: '0.9rem' }}>
            Manage your canonical product feeds
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
          <button
            className="button"
            onClick={() => setShowSyncModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              width: isMobile ? '100%' : 'auto',
              border: '1px solid var(--color-gray-300)',
              backgroundColor: 'white',
              color: 'var(--color-gray-700)',
            }}
          >
            <RefreshCw size={20} />
            Sync Feed
          </button>
          {/* Add Feed removed — feeds are created via integrations */}
        </div>
      </div>

      {feeds.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <Package size={48} style={{ margin: '0 auto 1rem', color: 'var(--color-gray-400)' }} />
          <h3 style={{ marginBottom: '0.5rem' }}>No Feeds</h3>
          <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem' }}>
            Add your first feed to start managing your product catalog
          </p>
          <button
            className="button button-primary"
            onClick={() => setShowCreateModal(true)}
          >
            Add Your First Feed
          </button>
        </div>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(350px, 100%), 1fr))', gap: '1.5rem' }}>
          {paginate(feeds, feedPage).map((feed: any) => (
            <div
              key={feed.feedId}
              onClick={() => setViewingFeed(feed.feedId)}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '1px solid var(--color-gray-200)',
                padding: '1.5rem',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
                e.currentTarget.style.borderColor = 'var(--color-sky-blue)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
                e.currentTarget.style.borderColor = 'var(--color-gray-200)'
              }}
            >
              {/* Status indicator bar */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  backgroundColor:
                    feed.status === 'active'
                      ? '#10b981'
                      : feed.status === 'draft'
                        ? '#f59e0b'
                        : '#6b7280',
                }}
              />

              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', marginTop: '0.25rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        backgroundColor: 'var(--color-sky-blue)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        flexShrink: 0,
                      }}
                    >
                      <Package size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: '1.125rem',
                          fontWeight: 600,
                          color: 'var(--color-gray-900)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {feed.merchant?.name || 'Unnamed Merchant'}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                        <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>
                          v{feed.feedVersion || feed.feed_version || '1.0.0'}
                        </p>
                        {feed.platform && feed.platform !== 'tixxly' && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              backgroundColor: '#e0e7ff',
                              color: '#4338ca',
                              letterSpacing: '0.025em',
                            }}
                            title={`Platform: ${feed.platform}`}
                          >
                            {feed.platform}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.375rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    backgroundColor:
                      feed.status === 'active'
                        ? '#d1fae5'
                        : feed.status === 'draft'
                          ? '#fef3c7'
                          : '#e5e7eb',
                    color:
                      feed.status === 'active'
                        ? '#065f46'
                        : feed.status === 'draft'
                          ? '#92400e'
                          : '#374151',
                    flexShrink: 0,
                  }}
                >
                  {feed.status}
                </span>
              </div>

              {/* Stats */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: 'var(--color-gray-50)',
                  borderRadius: '8px',
                }}
              >
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginBottom: '0.25rem', fontWeight: 500 }}>
                    Experiences
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-900)', marginBottom: '0.25rem' }}>
                    {feed.productCount || 0}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginBottom: '0.25rem', fontWeight: 500 }}>
                    Created
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-gray-700)' }}>
                    {new Date(feed.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '0.5rem',
                alignItems: 'center'
              }} onClick={(e) => e.stopPropagation()}>
                <a
                  href={`/vendor/inventory/feeds/${feed.feedId}/products`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    navigate(`/vendor/inventory/feeds/${feed.feedId}/products`)
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--color-sky-blue)',
                    textDecoration: 'none',
                    fontWeight: 500,
                    flex: 1,
                    justifyContent: isMobile ? 'center' : 'flex-start',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none'
                  }}
                >
                  <List size={16} />
                  View Experiences
                </a>
                <div style={{ display: 'flex', gap: '0.5rem', width: isMobile ? '100%' : 'auto' }}>
                  <button
                    onClick={() => setEditingFeedId(feed.feedId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.625rem',
                      borderRadius: '8px',
                      border: '1px solid var(--color-gray-300)',
                      backgroundColor: 'white',
                      color: 'var(--color-gray-700)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minWidth: '44px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'
                      e.currentTarget.style.borderColor = 'var(--color-gray-400)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white'
                      e.currentTarget.style.borderColor = 'var(--color-gray-300)'
                    }}
                    title="Edit Feed"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(feed)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.625rem',
                      borderRadius: '8px',
                      border: '1px solid #fee2e2',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minWidth: '44px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fee2e2'
                      e.currentTarget.style.borderColor = '#fecaca'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fef2f2'
                      e.currentTarget.style.borderColor = '#fee2e2'
                    }}
                    title="Delete Feed"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Pagination page={feedPage} totalItems={feeds.length} onPageChange={setFeedPage} />

      {/* Add/Edit Modal */}
      {(showCreateModal || editingFeedId) && (
        <InventoryFeedForm
          feed={editingFeedId ? editFeedData?.data?.data : undefined}
          isLoadingFeed={isLoadingEditFeed}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowCreateModal(false)
            setEditingFeedId(null)
          }}
          isLoading={createFeedMutation.isPending || updateFeedMutation.isPending}
          vendorId={vendorId ? parseInt(vendorId.toString()) : undefined}
          isMobile={isMobile}
        />
      )}

      {/* View Modal */}
      {viewingFeed && (
        <InventoryFeedViewModal
          feed={feedData?.data?.data}
          isLoading={isLoadingFeed}
          onClose={() => setViewingFeed(null)}
          isMobile={isMobile}
        />
      )}

      {/* Sync Feed Modal */}
      {showSyncModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => {
            if (!syncFeedMutation.isPending) {
              setShowSyncModal(false)
              setSelectedPlatform('')
            }
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: isMobile ? '100%' : '500px',
              width: '100%',
              padding: isMobile ? '1rem' : '1.5rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Sync Feed</h2>
              <button
                onClick={() => {
                  if (!syncFeedMutation.isPending) {
                    setShowSyncModal(false)
                    setSelectedPlatform('')
                  }
                }}
                disabled={syncFeedMutation.isPending}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.5rem',
                  minWidth: 'auto',
                  width: '2rem',
                  height: '2rem',
                  background: 'transparent',
                  border: 'none',
                  cursor: syncFeedMutation.isPending ? 'not-allowed' : 'pointer',
                  color: syncFeedMutation.isPending ? 'var(--color-gray-400)' : 'var(--color-sky-blue)',
                  opacity: syncFeedMutation.isPending ? 0.5 : 1,
                }}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Platform *
              </label>
              <select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="input"
                required
                style={{ width: '100%' }}
              >
                <option value="">Select a platform</option>
                <option value="bokun">Bokun</option>
                <option value="viator">Viator</option>
                <option value="getyourguide">GetYourGuide</option>
                <option value="klook">Klook</option>
              </select>
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.5rem' }}>
                Select the platform you want to sync feeds from. Make sure you have connected the integration first.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="button"
                onClick={() => {
                  setShowSyncModal(false)
                  setSelectedPlatform('')
                }}
                disabled={syncFeedMutation.isPending}
                style={{
                  border: '1px solid var(--color-gray-300)',
                  backgroundColor: 'white',
                  color: 'var(--color-sky-blue)',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button-primary"
                onClick={() => {
                  if (!selectedPlatform) {
                    setAlertModal({
                      isOpen: true,
                      type: 'error',
                      title: 'Error',
                      message: 'Please select a platform',
                    })
                    return
                  }
                  syncFeedMutation.mutate({ platform: selectedPlatform, vendorId: vendorId ? parseInt(vendorId.toString()) : undefined })
                }}
                disabled={syncFeedMutation.isPending || !selectedPlatform}
              >
                {syncFeedMutation.isPending ? 'Syncing...' : 'Sync Feed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={alertModal.onConfirm}
        onClose={() => setAlertModal({
          isOpen: false,
          type: 'info',
          title: '',
          message: ''
        })}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
      />
    </div>
  )
}

// Inventory Feed Form Component
function InventoryFeedForm({
  feed,
  isLoadingFeed,
  onSubmit,
  onCancel,
  isLoading,
  vendorId,
  isMobile = false,
}: {
  feed?: any
  isLoadingFeed?: boolean
  onSubmit: (data: any) => void
  onCancel: () => void
  isLoading: boolean
  vendorId?: number
  isMobile?: boolean
}) {
  // Fetch vendor data to populate merchant fields (only if not in feed response)
  const { data: vendorData } = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => api.vendors.get(vendorId!.toString()),
    enabled: !!vendorId && !feed?.merchant && !feed?.vendor, // Only fetch if creating new feed and vendor not in feed
  })

  // Use vendor from feed response if available, otherwise use vendorData
  const vendor = feed?.vendor || vendorData?.data?.data

  const [formData, setFormData] = useState(() => {
    // Initialize with safe defaults
    const defaultMerchant = {
      merchant_id: '',
      name: '',
      website_url: '',
      logo_url: '',
      contact: {
        email: '',
        phone: '',
        address: {
          street: '',
          city: '',
          state: '',
          postal_code: '',
          country_code: '',
        },
      },
      policies: {
        return_policy_url: '',
        return_window_days: 30,
        shipping_policy_url: '',
        terms_url: '',
        privacy_url: '',
      },
    }

    // If editing, merge with existing feed data safely
    if (feed?.merchant) {
      return {
        feed_version: feed.feed_version || '1.0.0',
        merchant: {
          merchant_id: feed.merchant.merchant_id || '',
          name: feed.merchant.name || '',
          website_url: feed.merchant.website_url || '',
          logo_url: feed.merchant.logo_url || '',
          contact: {
            email: feed.merchant.contact?.email || '',
            phone: feed.merchant.contact?.phone || '',
            address: {
              street: feed.merchant.contact?.address?.street || '',
              city: feed.merchant.contact?.address?.city || '',
              state: feed.merchant.contact?.address?.state || '',
              postal_code: feed.merchant.contact?.address?.postal_code || '',
              country_code: feed.merchant.contact?.address?.country_code || '',
            },
          },
          policies: {
            return_policy_url: feed.merchant.policies?.return_policy_url || '',
            return_window_days: feed.merchant.policies?.return_window_days || 30,
            shipping_policy_url: feed.merchant.policies?.shipping_policy_url || '',
            terms_url: feed.merchant.policies?.terms_url || '',
            privacy_url: feed.merchant.policies?.privacy_url || '',
          },
        },
        status: feed.status || 'draft',
        extensions: feed.extensions || {},
      }
    }

    return {
      feed_version: '1.0.0',
      merchant: defaultMerchant,
      status: 'draft',
      products: [],
      extensions: {},
    }
  })

  const [extensions, setExtensions] = useState<Array<{ key: string; value: string }>>(() => {
    if (feed?.extensions && typeof feed.extensions === 'object') {
      return Object.entries(feed.extensions).map(([key, value]) => ({
        key,
        value: typeof value === 'string' ? value : JSON.stringify(value),
      }))
    }
    return []
  })

  const [logoError, setLogoError] = useState(false)

  const [products, setProducts] = useState<any[]>(() => {
    if (feed?.products && Array.isArray(feed.products)) {
      return feed.products.map((p: any) => ({
        product_id_external: p.product_id_external || p.product_id || '',
        parent_id: p.parent_id || '',
        status: p.status || 'active',
        content: {
          title: p.content?.title || '',
          subtitle: p.content?.subtitle || '',
          description: p.content?.description || '',
          bullet_points: p.content?.bullet_points || [],
          tags: p.content?.tags || [],
          media: p.media || p.content?.media || [],
        },
        taxonomy: {
          category_path: p.taxonomy?.category_path || '',
          category_code: p.taxonomy?.category_code || '',
          brand: p.taxonomy?.brand || '',
          attributes: p.taxonomy?.attributes || [],
        },
        offers: p.offers || [{
          offer_id_external: '',
          merchant_listing_id: '',
          currency: 'USD',
          price: 0,
          availability: {
            status: 'in_stock',
          },
        }],
      }))
    }
    return []
  })

  // Reset logo error when logo URL changes
  useEffect(() => {
    setLogoError(false)
  }, [formData.merchant.logo_url])

  // Populate merchant from vendor data when creating new feed or when feed has vendor data
  useEffect(() => {
    if (vendor && !feed?.merchant) {
      const vendorAddress = vendor.contactInfo?.address || {}

      // Map country to country_code (ISO 3166-1 alpha-2)
      const countryCode = vendorAddress.country
        ? vendorAddress.country.length === 2
          ? vendorAddress.country.toUpperCase()
          : undefined // Could add country name to code mapping here
        : undefined

      setFormData(prev => ({
        ...prev,
        merchant: {
          merchant_id: vendor.vendorId?.toString() || '',
          name: vendor.companyName || '',
          website_url: vendor.website || '',
          logo_url: vendor.settings?.branding_settings?.logo || '',
          contact: {
            email: vendor.contactInfo?.email || '',
            phone: vendor.contactInfo?.phone || '',
            address: {
              street: vendorAddress.street || '',
              city: vendorAddress.city || '',
              state: vendorAddress.state || '',
              postal_code: vendorAddress.postal_code || vendorAddress.postalCode || '',
              country_code: countryCode || '',
            },
          },
          policies: prev.merchant.policies, // Keep existing policies or defaults
        },
      }))
    }
  }, [vendor, feed])

  // Update form data when feed loads (for editing)
  // Use merchant from feed (which is now populated from vendor in API)
  useEffect(() => {
    if (feed && !isLoadingFeed) {
      setFormData(prev => ({
        feed_version: feed.feed_version || prev.feed_version || '1.0.0',
        merchant: feed.merchant ? {
          merchant_id: feed.merchant.merchant_id || '',
          name: feed.merchant.name || '',
          website_url: feed.merchant.website_url || '',
          logo_url: feed.merchant.logo_url || '',
          contact: {
            email: feed.merchant.contact?.email || '',
            phone: feed.merchant.contact?.phone || '',
            address: {
              street: feed.merchant.contact?.address?.street || '',
              city: feed.merchant.contact?.address?.city || '',
              state: feed.merchant.contact?.address?.state || '',
              postal_code: feed.merchant.contact?.address?.postal_code || '',
              country_code: feed.merchant.contact?.address?.country_code || '',
            },
          },
          policies: {
            return_policy_url: feed.merchant.policies?.return_policy_url || '',
            return_window_days: feed.merchant.policies?.return_window_days || 30,
            shipping_policy_url: feed.merchant.policies?.shipping_policy_url || '',
            terms_url: feed.merchant.policies?.terms_url || '',
            privacy_url: feed.merchant.policies?.privacy_url || '',
          },
        } : prev.merchant,
        status: feed.status || prev.status || 'draft',
        extensions: feed.extensions || prev.extensions || {},
      }))

      // Update extensions
      if (feed.extensions && typeof feed.extensions === 'object') {
        setExtensions(
          Object.entries(feed.extensions).map(([key, value]) => ({
            key,
            value: typeof value === 'string' ? value : JSON.stringify(value),
          }))
        )
      } else {
        setExtensions([])
      }

      // Update products
      if (feed.products && Array.isArray(feed.products)) {
        setProducts(feed.products.map((p: any) => ({
          product_id_external: p.product_id_external || p.product_id || '',
          parent_id: p.parent_id || '',
          status: p.status || 'active',
          content: {
            title: p.content?.title || '',
            subtitle: p.content?.subtitle || '',
            description: p.content?.description || '',
            bullet_points: p.content?.bullet_points || [],
            tags: p.content?.tags || [],
            media: p.media || p.content?.media || [],
          },
          taxonomy: {
            category_path: p.taxonomy?.category_path || '',
            category_code: p.taxonomy?.category_code || '',
            brand: p.taxonomy?.brand || '',
            attributes: p.taxonomy?.attributes || [],
          },
          offers: p.offers || [{
            offer_id_external: '',
            merchant_listing_id: '',
            currency: 'USD',
            price: 0,
            availability: {
              status: 'in_stock',
            },
          }],
        })))
      }
    }
  }, [feed, isLoadingFeed])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Convert extensions array to object
    const extensionsObj: Record<string, any> = {}
    extensions.forEach(({ key, value }) => {
      if (key.trim()) {
        // Try to parse as JSON, otherwise use as string
        try {
          extensionsObj[key.trim()] = JSON.parse(value)
        } catch {
          extensionsObj[key.trim()] = value
        }
      }
    })

    onSubmit({
      ...formData,
      products,
      extensions: Object.keys(extensionsObj).length > 0 ? extensionsObj : undefined,
    })
  }

  const addExtension = () => {
    setExtensions([...extensions, { key: '', value: '' }])
  }

  const removeExtension = (index: number) => {
    setExtensions(extensions.filter((_, i) => i !== index))
  }

  const updateExtension = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...extensions]
    updated[index] = {
      ...updated[index],
      [field]: value,
    }
    setExtensions(updated)
  }



  // Show loading state while fetching feed data for editing
  if (isLoadingFeed && feed === undefined) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem',
        }}
        onClick={onCancel}
      >
        <div
          className="card"
          style={{
            maxWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: isMobile ? '1rem' : '1.5rem'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Skeleton */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <Skeleton height="1.5rem" width="200px" />
            <Skeleton height="2rem" width="2rem" style={{ borderRadius: '50%' }} />
          </div>

          {/* Form Fields Skeleton */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <Skeleton height="0.9rem" width="100px" style={{ marginBottom: '0.5rem' }} />
              <Skeleton height="2.5rem" width="100%" />
            </div>
            <div>
              <Skeleton height="0.9rem" width="60px" style={{ marginBottom: '0.5rem' }} />
              <Skeleton height="2.5rem" width="100%" />
            </div>
          </div>

          {/* Merchant Information Section Skeleton */}
          <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: 'var(--color-gray-50)', borderRadius: '8px' }}>
            <Skeleton height="1rem" width="180px" style={{ marginBottom: '1rem' }} />

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <Skeleton height="0.9rem" width="100px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
              <div>
                <Skeleton height="0.9rem" width="120px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <Skeleton height="0.9rem" width="90px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
              <div>
                <Skeleton height="0.9rem" width="110px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <Skeleton height="0.9rem" width="100px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
              <div>
                <Skeleton height="0.9rem" width="50px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="64px" width="64px" style={{ borderRadius: '8px' }} />
              </div>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <Skeleton height="0.95rem" width="80px" style={{ marginBottom: '0.75rem' }} />
              <Skeleton height="2.5rem" width="100%" style={{ marginBottom: '1rem' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <Skeleton height="0.9rem" width="60px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
              <div>
                <Skeleton height="0.9rem" width="100px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              <div>
                <Skeleton height="0.9rem" width="90px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
              <div>
                <Skeleton height="0.9rem" width="100px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
            </div>
          </div>

          {/* Policies Section Skeleton */}
          <div style={{ marginBottom: '2rem', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--color-gray-300)' }}>
            <Skeleton height="1rem" width="80px" style={{ marginBottom: '0.5rem' }} />
            <Skeleton height="0.75rem" width="250px" style={{ marginBottom: '1rem' }} />

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <Skeleton height="0.9rem" width="160px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
              <div>
                <Skeleton height="0.9rem" width="180px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              <div>
                <Skeleton height="0.9rem" width="160px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
              <div>
                <Skeleton height="0.9rem" width="130px" style={{ marginBottom: '0.5rem' }} />
                <Skeleton height="2.5rem" width="100%" />
              </div>
            </div>
          </div>

          {/* Extensions Section Skeleton */}
          <div style={{ marginBottom: '2rem', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--color-gray-300)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <Skeleton height="1rem" width="100px" />
                <Skeleton height="0.75rem" width="200px" style={{ marginTop: '0.25rem' }} />
              </div>
              <Skeleton height="1.5rem" width="120px" />
            </div>
            <div style={{ padding: '2rem', backgroundColor: 'var(--color-gray-50)', borderRadius: '4px', border: '2px dashed var(--color-gray-300)' }}>
              <Skeleton height="0.875rem" width="80%" style={{ margin: '0 auto' }} />
            </div>
          </div>

          {/* Submit Button Skeleton */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <Skeleton height="2.5rem" width="100px" style={{ borderRadius: '4px' }} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={onCancel}
    >
      <div
        className="card"
        style={{
          maxWidth: isMobile ? '100%' : '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: isMobile ? '1rem' : '1.5rem'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0 }}>
            {feed ? 'Edit Feed' : 'Add Feed'}
          </h2>
          <button
            onClick={onCancel}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.5rem',
              minWidth: 'auto',
              width: '2rem',
              height: '2rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-sky-blue)',
            }}
            aria-label="Close"
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.7'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Feed Version
              </label>
              <input
                type="text"
                value={formData.feed_version}
                onChange={(e) => setFormData({ ...formData, feed_version: e.target.value })}
                className="input"
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="input"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Merchant Information Section - Auto-populated from Vendor */}
          <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: 'var(--color-gray-50)', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1rem', color: 'var(--color-gray-700)' }}>
              Merchant Information
              <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: 'var(--color-gray-500)', marginLeft: '0.5rem' }}>
                (Auto-populated from vendor profile)
              </span>
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Merchant ID *
                </label>
                <input
                  type="text"
                  value={formData.merchant.merchant_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: { ...formData.merchant, merchant_id: e.target.value },
                    })
                  }
                  className="input"
                  required
                  disabled
                  style={{ backgroundColor: 'var(--color-gray-100)', cursor: 'not-allowed' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Merchant Name *
                </label>
                <input
                  type="text"
                  value={formData.merchant.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: { ...formData.merchant, name: e.target.value },
                    })
                  }
                  className="input"
                  required
                  disabled
                  style={{ backgroundColor: 'var(--color-gray-100)', cursor: 'not-allowed' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Website URL
                </label>
                <input
                  type="url"
                  value={formData.merchant.website_url || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: { ...formData.merchant, website_url: e.target.value },
                    })
                  }
                  className="input"
                  disabled
                  style={{ backgroundColor: 'var(--color-gray-100)', cursor: 'not-allowed' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Contact Email *
                </label>
                <input
                  type="email"
                  value={formData.merchant.contact?.email || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: {
                        ...formData.merchant,
                        contact: {
                          ...(formData.merchant.contact || {}),
                          email: e.target.value
                        },
                      },
                    })
                  }
                  className="input"
                  required
                  disabled
                  style={{ backgroundColor: 'var(--color-gray-100)', cursor: 'not-allowed' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.merchant.contact?.phone || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: {
                        ...formData.merchant,
                        contact: {
                          ...(formData.merchant.contact || {}),
                          phone: e.target.value
                        },
                      },
                    })
                  }
                  className="input"
                  disabled
                  style={{ backgroundColor: 'var(--color-gray-100)', cursor: 'not-allowed' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Logo
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
                  {formData.merchant.logo_url && !logoError ? (
                    <div
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid var(--color-gray-300)',
                        backgroundColor: 'var(--color-gray-50)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={formData.merchant.logo_url}
                        alt="Merchant Logo"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={() => setLogoError(true)}
                        onLoad={() => setLogoError(false)}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '8px',
                        border: '1px solid var(--color-gray-300)',
                        backgroundColor: 'var(--color-gray-100)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--color-gray-400)',
                        fontSize: '0.75rem',
                      }}
                    >
                      {formData.merchant.logo_url ? 'Error' : 'No Logo'}
                    </div>
                  )}
                  {formData.merchant.logo_url && (
                    <span
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--color-gray-600)',
                        wordBreak: 'break-all',
                        maxWidth: '100%',
                      }}
                      title={formData.merchant.logo_url}
                    >
                      {formData.merchant.logo_url.length > 40
                        ? `${formData.merchant.logo_url.substring(0, 40)}...`
                        : formData.merchant.logo_url}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', fontWeight: 'bold', color: 'var(--color-gray-700)' }}>
                Address
              </h4>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Street Address
              </label>
              <input
                type="text"
                value={formData.merchant.contact?.address?.street || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    merchant: {
                      ...formData.merchant,
                      contact: {
                        ...(formData.merchant.contact || {}),
                        address: {
                          ...(formData.merchant.contact?.address || {}),
                          street: e.target.value
                        }
                      },
                    },
                  })
                }
                className="input"
                disabled
                style={{ backgroundColor: 'var(--color-gray-100)', cursor: 'not-allowed', width: '100%' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  City
                </label>
                <input
                  type="text"
                  value={formData.merchant.contact?.address?.city || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: {
                        ...formData.merchant,
                        contact: {
                          ...(formData.merchant.contact || {}),
                          address: {
                            ...(formData.merchant.contact?.address || {}),
                            city: e.target.value
                          }
                        },
                      },
                    })
                  }
                  className="input"
                  disabled
                  style={{ backgroundColor: 'var(--color-gray-100)', cursor: 'not-allowed' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  State/Province
                </label>
                <input
                  type="text"
                  value={formData.merchant.contact?.address?.state || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: {
                        ...formData.merchant,
                        contact: {
                          ...(formData.merchant.contact || {}),
                          address: {
                            ...(formData.merchant.contact?.address || {}),
                            state: e.target.value
                          }
                        },
                      },
                    })
                  }
                  className="input"
                  disabled
                  style={{ backgroundColor: 'var(--color-gray-100)', cursor: 'not-allowed' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Postal Code
                </label>
                <input
                  type="text"
                  value={formData.merchant.contact?.address?.postal_code || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: {
                        ...formData.merchant,
                        contact: {
                          ...(formData.merchant.contact || {}),
                          address: {
                            ...(formData.merchant.contact?.address || {}),
                            postal_code: e.target.value
                          }
                        },
                      },
                    })
                  }
                  className="input"
                  disabled
                  style={{ backgroundColor: 'var(--color-gray-100)', cursor: 'not-allowed' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Country Code
                </label>
                <input
                  type="text"
                  value={formData.merchant.contact?.address?.country_code || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: {
                        ...formData.merchant,
                        contact: {
                          ...(formData.merchant.contact || {}),
                          address: {
                            ...(formData.merchant.contact?.address || {}),
                            country_code: e.target.value
                          }
                        },
                      },
                    })
                  }
                  className="input"
                  disabled
                  style={{ backgroundColor: 'var(--color-gray-100)', cursor: 'not-allowed' }}
                  placeholder="e.g., US, GB, CA"
                />
              </div>
            </div>
          </div>

          {/* Policies Section */}
          <div style={{ marginBottom: '2rem', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--color-gray-300)' }}>
            <h3 style={{ margin: 0, marginBottom: '1rem', fontSize: '1rem' }}>Policies</h3>
            <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginBottom: '1rem' }}>
              Configure cancellation, terms, and privacy policies
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Cancellation Policy URL
                </label>
                <input
                  type="url"
                  value={formData.merchant.policies?.return_policy_url || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: {
                        ...formData.merchant,
                        policies: {
                          ...formData.merchant.policies,
                          return_policy_url: e.target.value,
                        },
                      },
                    })
                  }
                  className="input"
                  placeholder="https://example.com/cancellation"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Cancellation Window (Days)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.merchant.policies?.return_window_days || 30}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: {
                        ...formData.merchant,
                        policies: {
                          ...formData.merchant.policies,
                          return_window_days: parseInt(e.target.value) || 30,
                        },
                      },
                    })
                  }
                  className="input"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Terms of Service URL
                </label>
                <input
                  type="url"
                  value={formData.merchant.policies?.terms_url || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: {
                        ...formData.merchant,
                        policies: {
                          ...formData.merchant.policies,
                          terms_url: e.target.value,
                        },
                      },
                    })
                  }
                  className="input"
                  placeholder="https://example.com/terms"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Privacy Policy URL
                </label>
                <input
                  type="url"
                  value={formData.merchant.policies?.privacy_url || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      merchant: {
                        ...formData.merchant,
                        policies: {
                          ...formData.merchant.policies,
                          privacy_url: e.target.value,
                        },
                      },
                    })
                  }
                  className="input"
                  placeholder="https://example.com/privacy"
                />
              </div>
            </div>
          </div>

          {/* Extensions Section */}
          <div style={{ marginBottom: '2rem', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--color-gray-300)' }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'center',
              marginBottom: '1rem',
              gap: isMobile ? '0.75rem' : '0'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>Extensions</h3>
                <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                  Provider-specific metadata (e.g., extensions.openai.enable_search)
                </p>
              </div>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  addExtension()
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.875rem',
                  color: 'var(--color-sky-blue)',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none'
                }}
              >
                <Plus size={14} />
                Add Extension
              </a>
            </div>

            {extensions.length === 0 ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: 'var(--color-gray-50)',
                borderRadius: '4px',
                border: '2px dashed var(--color-gray-300)'
              }}>
                <p style={{ color: 'var(--color-gray-600)', margin: 0 }}>
                  No extensions added. Click "Add Extension" to add provider-specific metadata.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {extensions.map((ext, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: '0.5rem',
                      alignItems: isMobile ? 'stretch' : 'center',
                      padding: '0.75rem',
                      backgroundColor: 'var(--color-gray-50)',
                      borderRadius: '6px',
                      border: '1px solid var(--color-gray-200)',
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Key (e.g., openai.enable_search)"
                      value={ext.key}
                      onChange={(e) => updateExtension(index, 'key', e.target.value)}
                      className="input"
                      style={{ flex: 1, fontSize: '0.875rem', width: isMobile ? '100%' : 'auto' }}
                    />
                    <input
                      type="text"
                      placeholder="Value"
                      value={ext.value}
                      onChange={(e) => updateExtension(index, 'value', e.target.value)}
                      className="input"
                      style={{ flex: 1, fontSize: '0.875rem', width: isMobile ? '100%' : 'auto' }}
                    />
                    <button
                      type="button"
                      onClick={() => removeExtension(index)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        border: '1px solid #fee2e2',
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        minWidth: isMobile ? '100%' : '40px',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fee2e2'
                        e.currentTarget.style.borderColor = '#fecaca'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fef2f2'
                        e.currentTarget.style.borderColor = '#fee2e2'
                      }}
                      title="Remove Extension"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button type="submit" className="button button-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : feed ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Inventory Feed View Modal
function InventoryFeedViewModal({
  feed,
  isLoading,
  onClose,
  isMobile = false,
}: {
  feed?: any
  isLoading: boolean
  onClose: () => void
  isMobile?: boolean
}) {
  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
        onClick={onClose}
      >
        <div className="card" onClick={(e) => e.stopPropagation()}>
          <p>Loading feed...</p>
        </div>
      </div>
    )
  }

  if (!feed) {
    return null
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          maxWidth: isMobile ? '100%' : '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: isMobile ? '1rem' : '1.5rem'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Feed Details</h2>
          <button
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.5rem',
              minWidth: 'auto',
              width: '2rem',
              height: '2rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-sky-blue)',
            }}
            aria-label="Close"
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.7'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Merchant Information</h3>
          <div style={{ padding: '1rem', backgroundColor: 'var(--color-gray-50)', borderRadius: '4px' }}>
            <p><strong>Merchant ID:</strong> {feed.merchant?.merchant_id}</p>
            <p><strong>Name:</strong> {feed.merchant?.name}</p>
            <p><strong>Email:</strong> {feed.merchant?.contact?.email}</p>
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Feed Information</h3>
          <div style={{ padding: '1rem', backgroundColor: 'var(--color-gray-50)', borderRadius: '4px' }}>
            <p><strong>Version:</strong> {feed.feedVersion || feed.feed_version}</p>
            {feed.platform && (
              <p><strong>Platform:</strong> <span style={{ textTransform: 'capitalize' }}>{feed.platform}</span></p>
            )}
            <p><strong>Generated At:</strong> {new Date(feed.generatedAt || feed.generated_at).toLocaleString()}</p>
            <p><strong>Experiences:</strong> {feed.productCount || 0}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

